/**
 * VIP权限Context Provider
 * 提供全局的VIP权限状态管理
 */

import type React from 'react';
import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { vipApiService } from '../services/vipApi.js';
import { useAuthStore } from '../stores/auth.js';
import type {
  PermissionInfoResponse,
  VIPPermissions,
  UserUsage,
} from '../types/vip.js';
import { VIPLevel } from '../types/vip.js';

/**
 * VIP Context类型定义
 */
interface VIPContextType {
  // 权限信息
  permissions: PermissionInfoResponse | null;
  loading: boolean;
  error: Error | null;

  // 状态
  isVIP: boolean;
  isVVIP: boolean;
  isExpired: boolean;
  daysRemaining: number | null;

  // 方法
  refetch: () => Promise<void>;
  refresh: () => Promise<PermissionInfoResponse>;

  // 权限检查
  hasPermission: (feature: keyof VIPPermissions) => boolean;
  hasMCPToolPermission: (level: 'basic' | 'extended' | 'all') => boolean;
  checkLimit: (feature: 'role_slots' | 'memory_count' | 'kb_file_count') => boolean;
  getRemaining: (feature: 'role_slots' | 'memory_count' | 'kb_file_count') => number;

  // VIP信息
  getVIPLevel: () => { level: VIPLevel; code: string; name: string };
  getVIPInfo: () => {
    level: VIPLevel;
    code: string;
    name: string;
    expireAt: string | null;
    isExpired: boolean;
    daysRemaining: number | null;
  };
}

// 创建Context
const VIPContext = createContext<VIPContextType | undefined>(undefined);

/**
 * VIP Provider Props
 */
interface VIPProviderProps {
  children: ReactNode;
  initialPermissions?: PermissionInfoResponse | null;
}

/**
 * VIP Provider组件
 */
export function VIPProvider({ children, initialPermissions = null }: VIPProviderProps) {
  const [permissions, setPermissions] = useState<PermissionInfoResponse | null>(initialPermissions);
  const [loading, setLoading] = useState(!initialPermissions);
  const [error, setError] = useState<Error | null>(null);

  /**
   * 获取权限信息
   */
  const refetch = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await vipApiService.getPermissions();
      setPermissions(data);

      // 缓存到localStorage
      localStorage.setItem('vipPermissions', JSON.stringify(data));
    } catch (err) {
      setError(err as Error);
      console.error('获取VIP权限失败:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 刷新权限信息
   */
  const refresh = useCallback(async () => {
    try {
      setError(null);
      const data = await vipApiService.refreshPermissions();
      setPermissions(data);

      // 更新localStorage
      localStorage.setItem('vipPermissions', JSON.stringify(data));

      return data;
    } catch (err) {
      setError(err as Error);
      console.error('刷新VIP权限失败:', err);
      throw err;
    }
  }, []);

  /**
   * 检查是否有某个功能权限
   */
  const hasPermission = useCallback((feature: keyof VIPPermissions): boolean => {
    if (!permissions) return false;
    return permissions.permissions[feature] === true;
  }, [permissions]);

  /**
   * 检查MCP工具权限
   */
  const hasMCPToolPermission = useCallback((level: 'basic' | 'extended' | 'all'): boolean => {
    if (!permissions) return false;
    const userLevel = permissions.permissions.mcp_tools;

    if (userLevel === 'all') return true;
    if (userLevel === 'extended') return level === 'basic' || level === 'extended';
    return level === 'basic';
  }, [permissions]);

  /**
   * 检查是否达到限制
   */
  const checkLimit = useCallback((feature: 'role_slots' | 'memory_count' | 'kb_file_count'): boolean => {
    if (!permissions) return false;
    const limit = permissions.limits[feature];
    const current = permissions.usage[feature as keyof UserUsage] as number;

    // -1 表示无限制
    if (limit === -1) return true;

    return current < limit;
  }, [permissions]);

  /**
   * 获取剩余数量
   */
  const getRemaining = useCallback((feature: 'role_slots' | 'memory_count' | 'kb_file_count'): number => {
    if (!permissions) return 0;
    const limit = permissions.limits[feature];
    const current = permissions.usage[feature as keyof UserUsage] as number;

    // -1 表示无限制
    if (limit === -1) return -1;

    return Math.max(0, limit - current);
  }, [permissions]);

  /**
   * 获取VIP等级信息
   */
  const getVIPLevel = useCallback(() => {
    if (!permissions) {
      return { level: VIPLevel.FREE, code: 'free', name: '免费用户' };
    }
    return {
      level: permissions.vipLevel,
      code: permissions.vipLevelCode,
      name: permissions.vipLevelName,
    };
  }, [permissions]);

  /**
   * 获取完整VIP信息
   */
  const getVIPInfo = useCallback(() => {
    if (!permissions) {
      return {
        level: VIPLevel.FREE,
        code: 'free',
        name: '免费用户',
        expireAt: null,
        isExpired: false,
        daysRemaining: null,
      };
    }
    return {
      level: permissions.vipLevel,
      code: permissions.vipLevelCode,
      name: permissions.vipLevelName,
      expireAt: permissions.vipExpireAt,
      isExpired: permissions.isExpired,
      daysRemaining: permissions.daysRemaining,
    };
  }, [permissions]);

  // 计算属性
  const isVIP = Boolean(permissions && permissions.vipLevel >= VIPLevel.VIP && !permissions.isExpired);
  const isVVIP = Boolean(permissions && permissions.vipLevel >= VIPLevel.VVIP && !permissions.isExpired);
  const isExpired = Boolean(permissions && permissions.isExpired);
  const daysRemaining = permissions?.daysRemaining ?? null;

  // 组件挂载时初始化（仅执行一次）
  useEffect(() => {
    // 从localStorage加载缓存的权限
    const cached = localStorage.getItem('vipPermissions');
    if (cached && !initialPermissions) {
      try {
        const cachedPermissions = JSON.parse(cached);
        setPermissions(cachedPermissions);
        setLoading(false);
      } catch (err) {
        console.error('解析缓存的权限信息失败:', err);
      }
    }

    // 从服务器获取最新权限（仅在用户已登录时）
    const isAuthenticated = useAuthStore.getState().isAuthenticated;
    if (isAuthenticated) {
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // 空依赖数组，仅在挂载时执行一次

  const value: VIPContextType = {
    permissions,
    loading,
    error,
    isVIP,
    isVVIP,
    isExpired,
    daysRemaining,
    refetch,
    refresh,
    hasPermission,
    hasMCPToolPermission,
    checkLimit,
    getRemaining,
    getVIPLevel,
    getVIPInfo,
  };

  return <VIPContext.Provider value={value}>{children}</VIPContext.Provider>;
}

/**
 * 使用VIP Context的Hook
 */
export function useVIPContext(): VIPContextType {
  const context = useContext(VIPContext);
  if (context === undefined) {
    throw new Error('useVIPContext must be used within a VIPProvider');
  }
  return context;
}

/**
 * HOC:为组件注入VIP权限
 */
export function withVIP<P extends object>(
  Component: React.ComponentType<P & { vip: VIPContextType }>
): React.ComponentType<P> {
  return function WithVIPComponent(props: P) {
    const vip = useVIPContext();
    return <Component {...props} vip={vip} />;
  };
}
